<template>
    <application-modal
        v-model="billingModal"
        :recording="!recording"
    >
        <v-btn
            data-testid="open-billing-btn"
            class="open-billing-btn warning large white--text px-3"
            block
            slot="modal-trigger"
            @click.native="openBillingPreferences"
        >
            <v-icon class="mr-2">autorenew</v-icon>
            Automatic Billing
        </v-btn>
        <div slot="modal-title">Automatic Billing Settings for Unit {{ ledger.facility_unit.name }}</div>
        <div slot="modal-body">
            <div class="text-h6" data-testid="enrolled-advisory">
                Tenant is{{ enrolled ? '' : ' not' }} enrolled in automatic billing on Unit {{ ledger.facility_unit.name }}.
            </div>
            <v-layout
                v-if="loading"
                justify-center
                data-testid="load-state"
            >
                <v-progress-circular
                    :size="70"
                    :width="7"
                    indeterminate
                />
            </v-layout>
            <v-layout v-else data-testid="billing-layout">
                <v-flex v-if="enrolled">
                    <div class="mt-3 pb-2">
                        <div class="subheading">Select an option:</div>
                        <v-radio-group
                            class="mt-0"
                            v-model="enrolledRadio"
                            row
                            hide-details
                            :disabled="!recording"
                            data-testid="select-group"
                        >
                            <v-radio
                                data-testid="payment-update-autobill-radio"
                                class="payment-update-autobill-radio"
                                label="Update Information"
                                value="1"/>
                            <v-radio
                                data-testid="payment-disable-autobill-radio"
                                class="payment-disable-autobill-radio"
                                label="Disable Autobill"
                                value="0"
                            />
                        </v-radio-group>
                        <div
                            v-if="enrolledRadio >= 0"
                            class="subheading mt-4"
                            data-testid="select-advisory"
                        >
                            <span v-if="enrolledRadio === '0'" data-testid="disable-selected">
                                Click "Submit" to disable automatic billing. Tenant may re-enroll on active unit in good standing at any time.
                            </span>
                            <div v-if="enrolledRadio === '1'" data-testid="update-selected">
                                Update tenant's automatic billing information using this form.<span class="font-weight-bold"> Credit Card information required to submit update.</span>
                            </div>
                        </div>
                        <credit-card
                            v-if="enrolledRadio === '1'"
                            class="mt-3"
                            @reset="takeInfo = false"
                        />
                    </div>
                </v-flex>
                <v-flex v-else class="text-h6">
                    <v-btn
                        data-testid="enroll-autobill-btn"
                        class="enroll-autobill-btn success mt-3"
                        v-if="!takeInfo"
                        block
                        @click="takeInfo = true"
                    >
                      Enroll in Automatic Billing
                    </v-btn>
                    <credit-card
                        v-else
                        class="mt-3"
                        @reset="takeInfo = false"
                    />
                </v-flex>
            </v-layout>
        </div>
        <v-btn
            data-testid="submit-autobill-btn"
            class="submit-autobill-btn primary"
            slot="create-button"
            v-if="!enrolled"
            @click.native="submitAutoBill"
            :disabled="!recording || !canSubmit"
            :loading="autoBillRequestLoading"
        >
          Submit
        </v-btn>
        <v-btn
            data-testid="update-autobill-btn"
            class="update-autobill-btn primary"
            slot="create-button"
            v-else-if="enrolled && enrolledRadio == 1"
            @click.native="submitAutoBill"
            :disabled="!recording || !canSubmit"
            :loading="autoBillRequestLoading"
        >
          Update
        </v-btn>
        <v-btn
            data-testid="disable-autobill-btn"
            class="disable-autobill-btn primary"
            slot="create-button"
            v-else-if="enrolled && enrolledRadio == 0"
            @click.native="disableAutoBill"
            :loading="autoBillRequestLoading"
        >
          Disable
        </v-btn>
    </application-modal>
</template>

<script>
/* eslint-disable eqeqeq */
import ApplicationModal from "@/components/ApplicationModal";
import CreditCard from "@/components/CreditCard";
import { mapState } from "vuex";

export default {
  name: "UpdateBilling",
  props: {
    ledger: {
      type: [Object, String]
    }
  },
  data() {
    return {
      loading: false,
      autoBillRequestLoading: false,
      billingModal: false,
      takeInfo: false,
      enrolledRadio: -1
    };
  },
  methods: {
    openBillingPreferences() {
      this.billingModal = true;
      this.takeInfo = false;
    },

    formatCC(ccNumber) {
      if (ccNumber && typeof ccNumber.replace === "function") {
        return ccNumber.replace(new RegExp(" ", "g"), "-");
      }
    },

    submitAutoBill() {
      this.autoBillRequestLoading = true;

      let payload = {
        auto_pay_type: "credit_card",
        cc_type: this.creditCard.ccType,
        cc_number: this.formatCC(this.creditCard.ccNumber),
        cc_cvv: this.creditCard.ccCVV,
        cc_exp_year: `${this.creditCard.ccExpirationYear}`,
        cc_exp_month: this.creditCard.ccExpirationMonth,
        billing_first_name: this.creditCard.firstName,
        billing_last_name: this.creditCard.lastName,
        billing_address1: this.creditCard.billingAddress,
        billing_zip: this.creditCard.billingZip,
        ledgerId: parseInt(this.ledger.pms_ledger_id),
        tenantId: parseInt(this.$route.params.tenantId),
        facilityId: this.$route.params.facilityId,
        external_unit_id: this.ledger.facility_unit.name
      };

      this.$store.dispatch("updateBillingPreferences", payload)
        .then(() => {
          this.$emit("success");
          this.$store.dispatch("handleSuccess", "Billing preferences updated successfully!");
          this.$store.commit("clearCreditCard");
          this.billingModal = false;
          this.autoBillRequestLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.autoBillRequestLoading = false;
        });
    },

    disableAutoBill() {
      this.autoBillRequestLoading = true;

      this.$store.dispatch("updateBillingPreferences", this.disable)
        .then(resp => {
          this.$emit("success");
          this.$store.dispatch("handleSuccess", "Billing preferences updated successfully!");
          this.billingModal = false;
          this.autoBillRequestLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.autoBillRequestLoading = false;
        });
    }
  },
  computed: {
    ...mapState({
      creditCard: state => state.tenant.creditCard,
      recording: state => state.call.recording,
      tenant: state => state.tenant.info
    }),

    enrolled() {
      return this.ledger ? (this.ledger.autobill === 1) : false;
    },

    canSubmit() {
      return (
        this.takeInfo &&
                this.creditCard.firstName &&
                this.creditCard.lastName &&
                this.creditCard.billingAddress &&
                this.creditCard.billingState &&
                this.creditCard.billingZip.length === 5 &&
                this.creditCard.ccType &&
                (this.creditCard.ccNumber.length >= 17 && this.creditCard.ccNumber.length <= 19) &&
                this.creditCard.ccExpirationMonth &&
                this.creditCard.ccExpirationYear &&
                (this.creditCard.ccCVV.length >= 3 && this.creditCard.ccCVV.length <= 4)
      );
    },

    disable() {
      if (this.ledger) {
        return {
          auto_pay_type: "none",
          ledgerId: parseInt(this.ledger.pms_ledger_id),
          tenantId: parseInt(this.$route.params.tenantId),
          facilityId: this.$route.params.facilityId,
          external_unit_id: this.ledger.facility_unit.name
        };
      } else {
        return {};
      }
    }
  },
  watch: {
    enrolledRadio() {
      if (this.enrolledRadio == 1) {
        this.takeInfo = true;
      };
    }
  },
  components: {
    ApplicationModal,
    CreditCard
  }
};
</script>
